import { FormRow, InputDatePicker, Select } from "@components/ui-kit";
import { t } from "@lingui/macro";
import {
  useProjectManagerOptionsForUsersQuery,
  useReassignActiveProjectsAndOwnedInvoicesMutation,
  useScheduleUserArchivalMutation,
} from "@src/__generated__/graphql";
import { ModalConfirm, type ModalConfirmProps } from "@src/components/ui-kit";
import { onError } from "@src/utils/apollo";
import { toApiDateTime } from "@src/utils/dates";
import { fieldToSelectProps } from "@src/utils/forms/inputHelpers";
import { required } from "@src/utils/forms/validators";
import { FieldState } from "formstate";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";

type ModalReassignActiveAssetsProps = Pick<
  ModalConfirmProps,
  "isOpen" | "onClose" | "onConfirm"
> & {
  userId: string;
  contextType: "deactivate" | "remove";
};

const ModalReassignActiveAssets = ({
  userId,
  onConfirm,
  contextType,
  ...props
}: ModalReassignActiveAssetsProps) => {
  const [to_user_id] = useState(() => new FieldState("").validators(required));
  const [archived_at] = useState(() =>
    new FieldState<Date | undefined>(new Date()).validators(required),
  );

  /**
   * Reset field on close
   */
  useEffect(() => {
    if (props.isOpen === false) {
      to_user_id.reset("");
      archived_at.reset(new Date());
    }
  }, [props.isOpen]);

  /**
   * Fetch options
   */
  const { data, loading } = useProjectManagerOptionsForUsersQuery({
    fetchPolicy: "cache-and-network",
    skip: !props.isOpen,
  });

  const onBeforeConfirm = async () => {
    const { hasError } = await to_user_id.validate();
    if (!hasError) {
      reassign({ variables: { fromUser: userId, toUser: to_user_id.value } });
    }
    if (contextType === "remove") return;
    const { hasError: archivedAtHasError } = await archived_at.validate();
    if (!archivedAtHasError) {
      archiveUser({
        variables: {
          id: userId,
          date: !archived_at.value
            ? undefined
            : toApiDateTime(archived_at.value),
        },
      });
    }
  };

  const [archiveUser, { loading: archiveLoading }] =
    useScheduleUserArchivalMutation({
      ...onError(),
      onCompleted(result) {
        if (result.scheduleUserArchival) {
          onConfirm();
          props.onClose();
        }
      },
    });

  const [reassign, { loading: loadingReassign }] =
    useReassignActiveProjectsAndOwnedInvoicesMutation({
      onCompleted(result) {
        if (result.reassignActiveProjectsAndOwnedInvoices) {
          onConfirm();
          props.onClose();
        }
      },
      ...onError(),
    });

  const projectManagerOptions = useMemo(
    () =>
      data?.projectManagerSimpleMap
        .filter((i) => i.id !== userId)
        .map((i) => ({
          label: `${i.full_name} (${i.codename})`,
          value: i.id,
        })) || [],
    [data?.projectManagerSimpleMap, userId],
  );

  return (
    <ModalConfirm
      isLoading={loadingReassign || archiveLoading}
      closeOnConfirm={false}
      onConfirm={onBeforeConfirm}
      size="2xl"
      {...props}
    >
      {contextType === "deactivate" ? (
        <p>{t`Before deactivating this user, please reassign their active
                projects or invoices. Select a new project manager below.`}</p>
      ) : (
        <p>{t`Before removing this user, please reassign their active
                projects or invoices. Select a new project manager below.`}</p>
      )}
      <p className="mb-16">{t`I would recommend you change the project manager on the projects or on the outgoing invoices.`}</p>
      <FormRow title={t`Switch these projects to:`}>
        <Select
          asPortal
          isLoading={loading}
          {...fieldToSelectProps(to_user_id, projectManagerOptions)}
        />
      </FormRow>
      {contextType === "deactivate" && (
        <FormRow title={t`Deactivate at:`}>
          <InputDatePicker
            selected={archived_at.value}
            onChange={(val) =>
              archived_at.onChange(val ? val.start : undefined)
            }
          />
        </FormRow>
      )}
    </ModalConfirm>
  );
};

export default observer(ModalReassignActiveAssets);
